import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MotionConfig } from 'motion/react'
import { QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App'
import { initI18n } from './i18n'
import { queryClient } from './lib/queryClient'
import { AuthProvider } from './state/auth'
import { CartProvider } from './state/cart'
import { PrefsProvider } from './state/prefs'
import { registerServiceWorker } from './pwa/register'
import { dismissBoot, showBootFailure } from './boot'
import { captureError, initTelemetry } from './lib/telemetry'

/* Before the i18n await, deliberately. The rejection handler below is the one
   failure this app has that shows the user a dead boot screen, and it is
   invisible from the outside — nobody reports "the loading spinner never
   stopped", they close the tab. Installing the handlers first means a throw
   anywhere in startup is already being listened for. */
initTelemetry({
  /* Every Remote Config key the app reads, with the value it falls back to
     when the fetch has not landed or has failed. Declared here rather than at
     the call sites so there is one list of what is switchable. */
  flags: {
    hex_booking_enabled: 'true',
    hex_maintenance_note: '',
  },
})

/* i18next must be ready before the first render, or components mount against
   an empty resource store and paint raw key paths — `nav.account` — at the
   user. The boot screen inlined in index.html is what stands in front of that
   await, and it is dismissed from here once there is something to show. */
initI18n().then(
  (locale) => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <BrowserRouter>
          {/* `reducedMotion="user"` makes every Motion component in the tree
              respect the OS setting without each one asking. Components still
              branch on `useReducedMotion()` where the *shape* of the animation
              should change rather than just its distance. */}
          <MotionConfig reducedMotion="user">
            <QueryClientProvider client={queryClient}>
              <PrefsProvider initialLocale={locale}>
                <AuthProvider>
                  <CartProvider>
                    <App />
                  </CartProvider>
                </AuthProvider>
              </PrefsProvider>
            </QueryClientProvider>
          </MotionConfig>
        </BrowserRouter>
      </StrictMode>,
    )

    dismissBoot()

    /* Registered after the first paint, deliberately. A service worker
       installing during startup competes with the route chunk for the same
       connection, and the only thing it can offer a first-time visitor is a
       slower first render. */
    registerServiceWorker()
  },
  (err: unknown) => {
    console.error('[hexclean] i18n failed to initialise', err)
    /* The app never renders, so no boundary will ever see this and nothing
       downstream will report it. Captured by hand, and flushed on the way out
       — the customer is looking at a permanently stalled boot screen and the
       only evidence it happened is this call. */
    captureError(err, { kind: 'error', context: { phase: 'boot', stage: 'i18n' } })
    showBootFailure()
  },
)
